import { useQuery } from '@tanstack/react-query';
import type { TemplateUpdateCheckResult } from '@volley/crucible-ipc-types/ipc-channels';

/**
 * Cache policy for the template-update check.
 *
 * The badge has to reflect reality even when the game's `crucible.json`
 * is mutated out-of-band — e.g. when the user runs `crucible update
 * <gameId>` from the CLI while the GUI is open. Without aggressive
 * refetching the cached `hasUpdate: true` would linger on the
 * dashboard for up to 5 minutes after the game had already been
 * updated to the latest template (the historical staleTime).
 *
 * - `staleTime: 60s` keeps repeated mounts within a single dashboard
 *   pass cheap (the IPC handler shells out to `git ls-remote`).
 * - `refetchOnMount: 'always'` fires a fresh fetch every time a badge
 *   re-mounts (e.g. navigating Workspace → Dashboard), bypassing
 *   staleness so out-of-band CLI updates can't survive a navigation.
 * - `refetchOnWindowFocus: 'always'` fires a fresh fetch when the
 *   user alt-tabs back to the GUI from a terminal — the canonical
 *   path that triggered this fix.
 *
 * The IPC handler reads `crucible.json` from disk on every call, so
 * each refetch sees ground truth.
 */
export const TEMPLATE_UPDATE_CHECK_OPTIONS = {
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: false,
} as const;

/**
 * Check whether `gameId`'s template has a newer version. Wraps the
 * `template:check-updates` IPC channel in a React Query cache.
 *
 * The `enabled` option is the gate for the Dashboard lazy-hydrate path
 * (`docs/dashboard-lazy-load-plan.md` line 116): the IPC handler walks
 * `git ls-remote` per call (network!), so cards below the fold must
 * NOT fire it. The Dashboard wires this via `useCardVisibility`.
 * Defaults to `true` so non-Dashboard callers (Workspace, etc.) keep
 * the prior eager behaviour.
 */
export function useTemplateUpdateCheck(
    gameId: string | undefined,
    opts?: { enabled?: boolean },
): {
    data: TemplateUpdateCheckResult | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => void;
} {
    const externalEnabled = opts?.enabled ?? true;
    const query = useQuery<TemplateUpdateCheckResult>({
        queryKey: ['template:check-updates', gameId],
        queryFn: async () => {
            if (!gameId) throw new Error('gameId is required');
            return await window.crucible.invoke('template:check-updates', { gameId });
        },
        enabled: externalEnabled && typeof gameId === 'string' && gameId.length > 0,
        ...TEMPLATE_UPDATE_CHECK_OPTIONS,
    });
    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error,
        refetch: () => { void query.refetch(); },
    };
}
